import { gql } from '@apollo/client';

export const GET_DASHBOARD_SUMMARY = gql`
  query GetDashboardSummary($date: String!, $timezone: String!, $revenuePeriod: String, $sessionsPeriod: String) {
    dashboardSummary(date: $date, timezone: $timezone) {
      revenue(period: $revenuePeriod) {
        period
        total
        deltaPct
        sparkline
        hasAnyRevenue
      }
      sessions(period: $sessionsPeriod) {
        period
        booked
        capacity
        deltaVsPrevious
        byDay {
          date
          count
        }
      }
      activeClients(windowDays: 30) {
        windowDays
        total
        newInWindow
        firstTime
        returning
      }
    }
  }
`;

export const GET_DASHBOARD_SESSIONS = gql`
  query GetDashboardSessions($startDate: String!, $endDate: String!) {
    dashboardSessions(startDate: $startDate, endDate: $endDate) {
      id
      date
      startTime
      durationMinutes
      format
      clientName
      clientIsNew
      clientNote
      alertNote
      matter
      providerName
      status
      fee
      feeIsEstimate
    }
  }
`;

export const GET_DASHBOARD_ATTENTION_ITEMS = gql`
  query GetDashboardAttentionItems($limit: Int) {
    dashboardAttentionItems(limit: $limit) {
      id
      kind
      title
      detail
      occurredAt
      refId
    }
  }
`;

export const GET_DASHBOARD_TEAM_CAPACITY = gql`
  query GetDashboardTeamCapacity($date: String!) {
    dashboardTeamCapacity(date: $date) {
      providerId
      name
      booked
      capacity
    }
  }
`;

export const GET_DASHBOARD_REVENUE_TREND = gql`
  query GetDashboardRevenueTrend($days: Int, $timezone: String!, $period: String) {
    dashboardRevenueTrend(days: $days, timezone: $timezone, period: $period) {
      points {
        date
        total
      }
      currentTotal
      previousTotal
      deltaPct
    }
  }
`;

export const GET_DASHBOARD_CONVERSION_FUNNEL = gql`
  query GetDashboardConversionFunnel($date: String!, $timezone: String!, $period: String) {
    dashboardConversionFunnel(date: $date, timezone: $timezone, period: $period) {
      leads
      booked
      completed
      paid
      medianTimeToBookHours
    }
  }
`;

export const GET_DASHBOARD_UTILIZATION_HEATMAP = gql`
  query GetDashboardUtilizationHeatmap($date: String!, $timezone: String!, $period: String, $providerId: String) {
    dashboardUtilizationHeatmap(date: $date, timezone: $timezone, period: $period, providerId: $providerId) {
      cells {
        day
        hour
        total
        booked
      }
    }
  }
`;

export const GET_DASHBOARD_OUTSTANDING_PAYMENTS = gql`
  query DashboardOutstandingPayments {
    dashboardOutstandingPayments {
      totalAmount
      count
      byState {
        state
        amount
        count
      }
    }
  }
`;

export const GET_DASHBOARD_PIPELINE_REVENUE = gql`
  query GetDashboardPipelineRevenue($date: String!, $timezone: String!, $period: String) {
    dashboardPipelineRevenue(date: $date, timezone: $timezone, period: $period) {
      totalProjected
      bookingCount
      byDay {
        date
        amount
        count
      }
    }
  }
`;

export const GET_DASHBOARD_FEE_ACCURACY = gql`
  query GetDashboardFeeAccuracy($date: String!, $timezone: String!, $period: String) {
    dashboardFeeAccuracy(date: $date, timezone: $timezone, period: $period) {
      totalEstimated
      totalActual
      delta
      deltaPct
      count
      overCount
      underCount
      matchCount
    }
  }
`;

export const GET_DASHBOARD_PROVIDER_LEADERBOARD = gql`
  query GetDashboardProviderLeaderboard($date: String!, $timezone: String!, $period: String) {
    dashboardProviderLeaderboard(date: $date, timezone: $timezone, period: $period) {
      providers {
        providerId
        name
        sessions
        revenue
        utilizationPct
        totalSlots
        bookedSlots
      }
    }
  }
`;

export const GET_DASHBOARD_TOP_CLIENTS = gql`
  query GetDashboardTopClients($limit: Int) {
    dashboardTopClients(limit: $limit) {
      clients {
        customerId
        clientId
        name
        totalRevenue
        bookingCount
        lastBookingDate
      }
    }
  }
`;

export const GET_DASHBOARD_DORMANT_CLIENTS = gql`
  query GetDashboardDormantClients($daysThreshold: Int, $limit: Int) {
    dashboardDormantClients(daysThreshold: $daysThreshold, limit: $limit) {
      clients {
        customerId
        clientId
        name
        lastBookingDate
        daysSinceLast
        bookingCount
      }
      count
    }
  }
`;

export const GET_DASHBOARD_CLIENT_RETENTION = gql`
  query GetDashboardClientRetention($date: String!, $timezone: String!, $period: String) {
    dashboardClientRetention(date: $date, timezone: $timezone, period: $period) {
      newCount
      returningCount
      buckets {
        bucket
        newCount
        returningCount
      }
    }
  }
`;

export const GET_DASHBOARD_METHOD_MIX = gql`
  query GetDashboardMethodMix($date: String!, $timezone: String!, $period: String) {
    dashboardMethodMix(date: $date, timezone: $timezone, period: $period) {
      totals {
        method
        count
      }
      series {
        bucket
        inPerson
        video
        phone
      }
      total
    }
  }
`;

export const GET_DASHBOARD_UPCOMING_PREVIEW = gql`
  query GetDashboardUpcomingPreview($timezone: String!, $days: Int) {
    dashboardUpcomingPreview(timezone: $timezone, days: $days) {
      days {
        date
        total
        confirmed
        unconfirmed
      }
      totalCount
    }
  }
`;

export const GET_DASHBOARD_CANCELLATION_RATE = gql`
  query GetDashboardCancellationRate($date: String!, $timezone: String!, $period: String) {
    dashboardCancellationRate(date: $date, timezone: $timezone, period: $period) {
      cancelledCount
      totalCount
      rate
      previousRate
    }
  }
`;

export const GET_DASHBOARD_REVENUE_BREAKDOWN = gql`
  query GetDashboardRevenueBreakdown($date: String!, $timezone: String!, $period: String) {
    dashboardRevenueBreakdown(date: $date, timezone: $timezone, period: $period) {
      byService {
        id
        name
        total
      }
      byProvider {
        id
        name
        total
      }
    }
  }
`;
